import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Receipt, Brain, Loader2, AlertCircle } from 'lucide-react';
import { extractTextFromImage } from '../../lib/ai/documentProcessing';
import { analyzeExpensePatterns, categorizePurchases } from '../../lib/ai/expenseAnalysis';

interface AnalysisResult {
  category: string;
  amount: number;
  date: string;
  vendor: string;
  taxDeductible: boolean;
  confidence: number;
}

export function SmartReceiptAnalyzer() {
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onDrop = async (acceptedFiles: File[]) => {
    setAnalyzing(true);
    setError(null);
    try {
      const file = acceptedFiles[0];
      const text = await extractTextFromImage(file);
      const [categoryData, patterns] = await Promise.all([
        categorizePurchases([{ text }]),
        analyzeExpensePatterns([{ text }]),
      ]);

      setResults({
        ...categoryData,
        confidence: 0.95,
      });
    } catch (err) {
      console.error('Error analyzing receipt:', err);
      setError('Failed to analyze receipt. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
    maxFiles: 1,
  });

  return (
    <div className="bg-dark-card rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Receipt className="h-6 w-6 text-primary-400" />
          <h2 className="text-xl font-semibold text-dark-text">Smart Receipt Analysis</h2>
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary-400 bg-primary-400/10' : 'border-dark-border'}
          ${analyzing ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <input {...getInputProps()} />
        {analyzing ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 text-primary-400 animate-spin" />
            <p className="mt-4 text-dark-text">Analyzing receipt...</p>
          </div>
        ) : (
          <>
            <Brain className="h-12 w-12 text-dark-muted mx-auto mb-4" />
            <p className="text-dark-text font-medium">Drop receipt here or click to upload</p>
            <p className="text-dark-muted text-sm mt-2">Supports PNG, JPG, JPEG formats</p>
          </>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center text-red-400">
            <AlertCircle className="h-5 w-5 mr-2" />
            <p>{error}</p>
          </div>
        </div>
      )}

      {results && (
        <div className="mt-6 bg-dark-bg rounded-lg p-4">
          <h3 className="text-lg font-medium text-dark-text mb-4">Analysis Results</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-dark-muted">Category</p>
              <p className="text-dark-text font-medium">{results.category}</p>
            </div>
            <div>
              <p className="text-dark-muted">Amount</p>
              <p className="text-dark-text font-medium">${results.amount.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-dark-muted">Vendor</p>
              <p className="text-dark-text font-medium">{results.vendor}</p>
            </div>
            <div>
              <p className="text-dark-muted">Date</p>
              <p className="text-dark-text font-medium">{results.date}</p>
            </div>
            <div>
              <p className="text-dark-muted">Tax Deductible</p>
              <p className="text-dark-text font-medium">{results.taxDeductible ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-dark-muted">Confidence</p>
              <p className="text-dark-text font-medium">{(results.confidence * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
